import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import {
  Zap,
  User,
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeft,
  PanelRight,
  PanelRightClose,
  ChevronDown,
  Archive,
  Wifi,
  WifiOff,
  HelpCircle,
  Loader2,
  Check,
} from 'lucide-react';
import { apiService } from '../../services/api';
import MarkdownRenderer from '../Artifacts/MarkdownRenderer';
import { useTheme } from '../../context/ThemeContext';
import QuotaPill from '../Quota/QuotaPill';

interface HeaderProps {
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  onSidebarToggle: () => void;
  isSidebarOpen: boolean;
  onContextToggle: () => void;
  isContextOpen: boolean;
}

// W3 auth can leave us with a username equal to the W3 `cognito:username` claim,
// which for AWS Cognito pools is the raw UUID of the user — not something to
// show in a header. Treat UUID-looking usernames as unset and prefer the email
// prefix instead. Synthetic emails (w3key_<fingerprint>@w3.local) get trimmed
// too so we don't expose an internal fingerprint to the UI.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function displayNameFor(user: { username?: string; email?: string } | null | undefined): string {
  if (!user) return '';
  const uname = (user.username || '').trim();
  if (uname && !UUID_RE.test(uname) && !uname.startsWith('w3key_')) return uname;
  const emailLocal = (user.email || '').split('@')[0];
  if (emailLocal && !emailLocal.startsWith('w3key_')) return emailLocal;
  return 'משתמש W3';
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, isMenuOpen, onSidebarToggle, isSidebarOpen, onContextToggle, isContextOpen }) => {
  const { user, logout, mcpStatus } = useAuthContext();
  const { bgColor, setBgColor, options } = useTheme();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMarkdown, setHelpMarkdown] = useState<string | null>(null);
  const [helpLoading, setHelpLoading] = useState(false);

  const openHelp = async () => {
    setHelpOpen(true);
    if (helpMarkdown) return;
    setHelpLoading(true);
    try {
      const data = await apiService.getUserGuide();
      setHelpMarkdown(data.markdown);
    } catch (err) {
      console.error('Failed to load user guide', err);
      setHelpMarkdown('# שגיאה\n\nלא ניתן היה לטעון את המדריך. נסה שוב.');
    } finally {
      setHelpLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-12 bg-sidebar-950 border-b border-sidebar-700/50 flex items-center justify-between px-3 flex-shrink-0 z-20">
      {/* Left: sidebar toggle + logo */}
      <div className="flex items-center gap-2">
        {/* Mobile menu button */}
        <button
          onClick={onMenuToggle}
          className="md:hidden p-1.5 rounded-md text-sidebar-400 hover:text-white hover:bg-sidebar-800 press-sm"
        >
          {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>

        {/* Desktop sidebar toggle */}
        <button
          onClick={onSidebarToggle}
          className="hidden md:flex p-1.5 rounded-md text-sidebar-400 hover:text-white hover:bg-sidebar-800 press-sm"
          title={`${isSidebarOpen ? 'הסתר סייד-בר' : 'הצג סייד-בר'} (⌘B)`}
        >
          {isSidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
        </button>

        {/* Logo */}
        <div className="flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-primary-400" />
          <span className="text-sm font-semibold text-white hidden sm:inline">Financial AI</span>
        </div>
      </div>

      {/* Right: user menu */}
      {user && (
        <div className="relative flex items-center gap-2" ref={dropdownRef}>
          <QuotaPill />
          {/* Context panel (Sources / artifacts) toggle — the left-edge third column */}
          <button
            onClick={onContextToggle}
            className={`hidden md:flex p-1.5 rounded-md press-sm ${
              isContextOpen
                ? 'text-white bg-sidebar-800'
                : 'text-sidebar-400 hover:text-white hover:bg-sidebar-800'
            }`}
            title={`${isContextOpen ? 'הסתר פאנל מקורות' : 'הצג פאנל מקורות'} (⌘.)`}
            aria-label="הצג/הסתר פאנל מקורות"
            aria-pressed={isContextOpen}
          >
            {isContextOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
          </button>
          <button
            onClick={openHelp}
            className="p-1.5 rounded-md text-sidebar-400 hover:text-white hover:bg-sidebar-800 press-sm"
            title="עזרה ומדריך"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <div
            className={`hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border ${
              mcpStatus?.connected
                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
            }`}
            title={mcpStatus?.connected ? 'MCP מחובר' : 'MCP לא מחובר'}
          >
            {mcpStatus?.connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            <span>
              {mcpStatus?.connected
                ? `MCP מחובר${typeof mcpStatus.tool_count === 'number' ? ` • ${mcpStatus.tool_count} כלים` : ''}`
                : 'MCP לא מחובר'}
            </span>
          </div>

          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 p-1.5 rounded-md text-sidebar-300 hover:text-white hover:bg-sidebar-800 press-sm"
          >
            <div className="h-6 w-6 bg-primary-600/30 rounded-full flex items-center justify-center">
              <User className="h-3.5 w-3.5 text-primary-400" />
            </div>
            <span className="text-xs hidden sm:inline">{displayNameFor(user)}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          {isDropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-64 max-h-[calc(100vh-3.5rem)] overflow-y-auto bg-sidebar-800 border border-sidebar-700 rounded-lg shadow-xl py-1 z-50 origin-top-left animate-chat-bubble motion-reduce:animate-none" dir="rtl">
              <div className="px-3 py-2 border-b border-sidebar-700">
                <p className="text-sm font-medium text-white">{displayNameFor(user)}</p>
                <p className="text-xs text-sidebar-400">{user.email}</p>
              </div>

              {/* Settings — theme color */}
              <div className="px-3 py-2 border-b border-sidebar-700">
                <p className="text-[11px] font-medium text-sidebar-400 mb-1.5">צבע ערכת נושא</p>
                <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup" aria-label="צבע ערכת נושא">
                  {options.map((o) => {
                    const selected = bgColor === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={o.label}
                        title={o.label}
                        onClick={() => setBgColor(o.id)}
                        className={`h-6 w-6 rounded-full flex items-center justify-center ring-2 ring-offset-2 ring-offset-sidebar-800 transition-all press-sm ${
                          selected ? 'ring-white' : 'ring-transparent hover:ring-sidebar-500'
                        }`}
                        style={{ backgroundColor: o.swatch }}
                      >
                        {selected && <Check className="h-3.5 w-3.5 text-white" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-sidebar-400 cursor-not-allowed"
                disabled
                title="בקרוב"
              >
                <Archive className="h-3.5 w-3.5" />
                <span>ארכיון שיחות</span>
                <span className="mr-auto text-[10px] bg-sidebar-600 text-sidebar-300 px-1 rounded">בקרוב</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-sidebar-700 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>התנתק</span>
              </button>
            </div>
          )}
        </div>
      )}
      {helpOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setHelpOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-[92vw] max-h-[85vh] flex flex-col"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2 text-gray-800">
                <HelpCircle className="h-5 w-5 text-primary-500" />
                <span className="text-sm font-semibold">עזרה ומדריך משתמש</span>
              </div>
              <button
                onClick={() => setHelpOpen(false)}
                className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                title="סגור"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto px-6 py-4">
              {helpLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : helpMarkdown ? (
                <MarkdownRenderer content={helpMarkdown} />
              ) : (
                <div className="text-center py-16 text-gray-400 text-sm">לא טעון</div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 flex-shrink-0 text-xs text-gray-500">
              <span>שאלות "איך עושים..." אפשר לשאול גם ישירות בצ'אט.</span>
              <button
                onClick={() => setHelpOpen(false)}
                className="px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
